import * as React from 'react';
import { InView } from 'react-intersection-observer';
import { useTransition, animated, UseTransitionProps } from 'react-spring';

import { Col, ProductTilePlaceholder } from '@notino/react-styleguide';
import { GetProductsByIdBatchedQuery } from '@notino/shared/definitions/types';

import ProductItem from '@components/Universals/ProductItem';
import {
  IProductOuterProps,
  IProductItemProps,
} from '@components/Universals/ProductItem/model';
import { ProductListHolder } from '@components/Universals/ProductListHolder';

import { WISHLIST_PAGE_SIZE } from '../constants';

import { useRegisteredWatchdog } from './WishlistPager/useRegisteredWatchdog';

const columns = { xs: 6, md: 3 };
const animationConfiguration = (enabled: boolean): UseTransitionProps => ({
  from: { opacity: 1 },
  enter: { opacity: 1 },
  leave: { opacity: 0 },
  keys: (item: GetProductsByIdBatchedQuery['vpProductByIds'][number]) =>
    item?.id,
  config: {
    duration: enabled ? 300 : 0,
    clamp: true,
  },
});

type AnimatedProductListProps = Pick<
  IProductItemProps,
  'canSpaRedirect' | 'showAddToCartModal'
> & {
  products: GetProductsByIdBatchedQuery['vpProductByIds'];
  productItemProps?: IProductOuterProps;
  isLoading?: boolean;
  disableAnimation?: boolean;
  isSharedWishlist?: boolean;
  onProductInView?: (
    product: GetProductsByIdBatchedQuery['vpProductByIds'][number]
  ) => void;
};

const AnimatedProductList = ({
  products,
  productItemProps = {},
  showAddToCartModal,
  canSpaRedirect,
  isLoading,
  disableAnimation,
  isSharedWishlist,
  onProductInView,
}: AnimatedProductListProps) => {
  const transitions = useTransition(
    products,
    React.useMemo(
      () => animationConfiguration(!disableAnimation),
      [disableAnimation]
    )
  );

  const { registeredEmails } = useRegisteredWatchdog({ products });

  return (
    <ProductListHolder>
      {transitions((style, product, _, index) => {
        const hasRegisteredWatchdog = Boolean(
          registeredEmails &&
            registeredEmails[product.catalogId] &&
            registeredEmails[product.catalogId].length > 0
        );

        return (
          <Col key={product.id} {...columns}>
            <animated.div style={style} key={product.id}>
              <InView
                threshold={0.5}
                onChange={(inView) => inView && onProductInView?.(product)}
              >
                <ProductItem
                  product={product}
                  hasRegisteredWatchdog={hasRegisteredWatchdog}
                  showWishlistButton={!isSharedWishlist}
                  position={index + 1}
                  showAddToCartModal={showAddToCartModal}
                  canSpaRedirect={canSpaRedirect}
                  useMasterUrl={false}
                  {...productItemProps}
                />
              </InView>
            </animated.div>
          </Col>
        );
      })}

      {isLoading &&
        new Array(WISHLIST_PAGE_SIZE).fill(0).map((_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Col key={index} {...columns}>
            <ProductTilePlaceholder />
          </Col>
        ))}
    </ProductListHolder>
  );
};

export default AnimatedProductList;
